import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { Message, User } from '../../types';
import webSocketService from '../../services/websocket';
import apiService from '../../services/api';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ChatHeader from './ChatHeader';
import UserList from './UserList';
import LoadingSpinner from '../UI/LoadingSpinner';

const ChatLayoutContainer = styled.div`
  display: flex;
  height: 100vh;
  background: #f8f9fa;
`;

const Sidebar = styled.div<{ isOpen: boolean }>`
  width: 300px;
  background: white;
  border-right: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 1000;
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    box-shadow: ${props => props.isOpen ? '2px 0 10px rgba(0,0,0,0.1)' : 'none'};
  }
`;

const MainChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ChatMessagesContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ErrorMessage = styled.div`
  background: #ffeaea;
  border: 1px solid #ffcbcb;
  color: #d63031;
  padding: 1rem;
  margin: 1rem;
  border-radius: 8px;
  text-align: center;
`;

const ConnectionStatus = styled.div<{ connected: boolean }>`
  background: ${props => props.connected ? '#eafff0' : '#ffeaea'};
  border: 1px solid ${props => props.connected ? '#c3f0d1' : '#ffcbcb'};
  color: ${props => props.connected ? '#00b894' : '#d63031'};
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  text-align: center;
  transition: all 0.3s ease;
`;

const TypingIndicator = styled.div`
  padding: 0.5rem 1rem;
  color: #666;
  font-style: italic;
  font-size: 0.9rem;
  min-height: 2rem;
  display: flex;
  align-items: center;
`;

const Overlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isVisible ? 'block' : 'none'};

  @media (min-width: 769px) {
    display: none;
  }
`;

interface ChatContainerProps {
  roomId?: string;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ roomId = 'general' }) => {
  const { state } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadMessages = useCallback(async (pageNum: number, reset: boolean = false) => {
    try {
      const response = await apiService.getMessages(roomId, pageNum, 50);
      if (response.success && response.data) {
        const newMessages = response.data.data;
        
        if (reset) {
          setMessages(newMessages.reverse()); // Reverse to show newest at bottom
        } else {
          setMessages(prev => [...newMessages.reverse(), ...prev]);
        }

        setHasMoreMessages(pageNum < response.data.totalPages);
        setPage(pageNum);
      }
    } catch (error: any) {
      console.error('Failed to load messages:', error);
      setError('Failed to load messages');
    }
  }, [roomId]);

  const initializeChat = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      // Join the room
      if (state.user) {
        webSocketService.joinRoom(roomId, state.user.id);
      }

      // Load initial messages
      await loadMessages(1, true);
    } catch (error: any) {
      setError(error.message || 'Failed to initialize chat');
    } finally {
      setIsLoading(false);
    }
  }, [state.user, roomId, loadMessages]);

  const handleTypingUpdate = useCallback((userId: string, isTyping: boolean) => {
    if (userId === state.user?.id) return; // Don't show our own typing

    setTypingUsers(prev => {
      const newSet = new Set(prev);
      if (isTyping) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });

    // Clear typing indicator after timeout
    if (isTyping) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setTypingUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      }, 3000);
    }
  }, [state.user?.id]);

  // Initialize WebSocket and load messages
  useEffect(() => {
    if (!state.user) return;

    initializeChat();
    return () => {
      webSocketService.leaveRoom();
    };
  }, [state.user, roomId, initializeChat]);

  // WebSocket event handlers
  useEffect(() => {
    if (!state.user) return;

    webSocketService.onMessage(handleNewMessage);
    webSocketService.onUserJoined(handleUserJoined);
    webSocketService.onUserLeft(handleUserLeft);
    webSocketService.onTyping(handleTypingUpdate);
    webSocketService.onConnection(setIsConnected);
    webSocketService.onError(handleWebSocketError);

    return () => {
      // Cleanup would be handled by the service disconnect
    };
  }, [state.user, handleTypingUpdate]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const handleNewMessage = (message: Message) => {
    setMessages(prev => [...prev, message]);
    
    // Remove typing indicator for this user
    if (message.userId) {
      setTypingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(message.userId);
        return newSet;
      });
    }
  };

  const handleUserJoined = (user: User) => {
    setUsers(prev => {
      const exists = prev.find(u => u.id === user.id);
      return exists ? prev : [...prev, user];
    });
  };

  const handleUserLeft = (user: User) => {
    setUsers(prev => prev.filter(u => u.id !== user.id));
    setTypingUsers(prev => {
      const newSet = new Set(prev);
      newSet.delete(user.id);
      return newSet;
    });
  };

  const handleWebSocketError = (error: string) => {
    setError(error);
  };

  const handleSendMessage = async (content: string, isAI: boolean = false) => {
    if (!state.user || !content.trim()) return;

    try {
      const messageData = {
        content: content.trim(),
        roomId,
      };

      // Send via API
      const response = await apiService.sendMessage(messageData);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to send message');
      }

      // If it's an AI request, also send to AI service
      if (isAI) {
        handleAIRequest(content);
      }

    } catch (error: any) {
      setError(error.message || 'Failed to send message');
    }
  };

  const handleAIRequest = async (content: string) => {
    try {
      // Get recent messages for context
      const recentMessages = messages.slice(-5);
      
      const response = await apiService.sendToAI({
        message: content,
        context: recentMessages,
      });

      if (response.success && response.data) {
        // AI response will be sent via WebSocket
        console.log('AI response received:', response.data);
      }
    } catch (error: any) {
      console.error('AI request failed:', error);
      // Don't show AI errors to user, just log them
    }
  };

  const handleTyping = (isTyping: boolean) => {
    webSocketService.sendTyping(isTyping);
  };

  const loadMoreMessages = () => {
    if (hasMoreMessages && !isLoading) {
      loadMessages(page + 1);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getTypingText = () => {
    const typingArray = Array.from(typingUsers);
    const typingNames = typingArray.map(userId => {
      const user = users.find(u => u.id === userId);
      return user?.username || 'Someone';
    });

    if (typingNames.length === 0) return '';
    if (typingNames.length === 1) return `${typingNames[0]} is typing...`;
    if (typingNames.length === 2) return `${typingNames[0]} and ${typingNames[1]} are typing...`;
    return `${typingNames[0]} and ${typingNames.length - 1} others are typing...`;
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading chat..." />;
  }

  return (
    <ChatLayoutContainer>
      <Overlay 
        isVisible={sidebarOpen} 
        onClick={() => setSidebarOpen(false)} 
      />
      
      <Sidebar isOpen={sidebarOpen}>
        <UserList 
          users={users} 
          currentUser={state.user!}
          onClose={() => setSidebarOpen(false)}
        />
      </Sidebar>

      <MainChatArea>
        <ChatHeader 
          roomName="General Chat"
          userCount={users.length}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onLogout={() => window.location.href = '/login'}
        />

        {!isConnected && (
          <ConnectionStatus connected={false}>
            Disconnected from server. Attempting to reconnect...
          </ConnectionStatus>
        )}

        {error && (
          <ErrorMessage>
            {error}
            <button 
              onClick={() => setError('')}
              style={{ marginLeft: '1rem', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
            >
              ✕
            </button>
          </ErrorMessage>
        )}

        <ChatMessagesContainer>
          <MessageList
            messages={messages}
            currentUser={state.user!}
            onLoadMore={loadMoreMessages}
            hasMore={hasMoreMessages}
            isLoading={isLoading}
          />
          
          <TypingIndicator>
            {getTypingText()}
          </TypingIndicator>
          
          <div ref={messagesEndRef} />
        </ChatMessagesContainer>

        <MessageInput
          onSendMessage={handleSendMessage}
          onTyping={handleTyping}
          disabled={!isConnected}
        />
      </MainChatArea>
    </ChatLayoutContainer>
  );
};

export default ChatContainer; 